import { MoreVertical } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type ActionMenuItem = {
  label: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  destructive?: boolean;
};

const MENU_WIDTH = 208;

/**
 * A "···" overflow menu for row actions. Keeps dense table rows scannable by
 * hiding secondary actions behind one click instead of stacking several buttons.
 *
 * The menu itself renders through a portal at document.body, positioned by the
 * trigger button's screen coordinates, so it's never clipped by an ancestor
 * with overflow-hidden/overflow-auto (e.g. the last row of a scrollable table).
 */
export function ActionMenu({ items, label = 'Actions' }: { items: ActionMenuItem[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const opensUpward = rect.bottom + 200 > window.innerHeight;
    setPosition({
      top: opensUpward ? rect.top - 4 : rect.bottom + 4,
      left: Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (
        buttonRef.current?.contains(event.target as Node) ||
        menuRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    // Scrolling inside the open menu itself must not close it -- only a scroll of some
    // ancestor (the page, a modal panel, a scrollable table, etc.) invalidates the
    // portal's position and should.
    const onScrollOrResize = (event: Event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const opensUpward = buttonRef.current
    ? buttonRef.current.getBoundingClientRect().bottom + 200 > window.innerHeight
    : false;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="grid size-11 place-items-center rounded-control border border-border text-ink/60 hover:bg-surface-muted hover:text-ink"
      >
        <MoreVertical className="size-4" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: MENU_WIDTH,
              transform: opensUpward ? 'translateY(-100%)' : undefined,
            }}
            className="schd-menu-panel z-50 rounded-control border border-border bg-surface py-1 shadow-card"
          >
            {items.map((item) =>
              item.href ? (
                <a
                  key={item.label}
                  role="menuitem"
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="flex min-h-10 w-full items-center px-3 text-sm text-ink hover:bg-surface-muted"
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    setOpen(false);
                    item.onClick?.();
                  }}
                  className={`flex min-h-10 w-full items-center px-3 text-left text-sm hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50 ${
                    item.destructive ? 'text-red-700' : 'text-ink'
                  }`}
                >
                  {item.label}
                </button>
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
